import React, { useState } from "react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { CURRENCIES } from "../lib/constants";
import { DealStatusControl } from "./DealStatusControl";
import { ChevronDown, ChevronRight, Plus, Check, Receipt, Trash2, Loader2, PenLine, Pause, Play, Clock, PauseCircle, AlertTriangle } from "lucide-react";

export function fmtMoney(v, currency) {
  if (v == null) return "—";
  try {
    return new Intl.NumberFormat("en-GB", { style: "currency", currency: currency || "NGN", maximumFractionDigits: 0 }).format(v);
  } catch {
    return `${currency || ""} ${Math.round(v).toLocaleString()}`;
  }
}

const BUCKET_BADGE = {
  signed: "bg-emerald-100 text-emerald-800 border-emerald-300",
  proposal: "bg-amber-100 text-amber-800 border-amber-300",
  on_hold: "bg-slate-100 text-slate-600 border-slate-300",
  lost: "bg-rose-50 text-rose-600 border-rose-200",
};
const BUCKET_LABEL = { signed: "Signed", proposal: "Proposal out", on_hold: "On hold", lost: "Lost" };

const PAY_BADGE = {
  Forecast: "bg-slate-100 text-slate-700 border-slate-300",
  Invoiced: "bg-blue-100 text-blue-800 border-blue-300",
  Collected: "bg-emerald-100 text-emerald-800 border-emerald-300",
  "Written Off": "bg-rose-50 text-rose-600 border-rose-200",
  Deferred: "bg-amber-50 text-amber-700 border-amber-200",
};

function PaymentLine({ p, deal, onUpdate, onDelete, onOverride, busy }) {
  const [editingDate, setEditingDate] = useState(false);
  const [date, setDate] = useState(p.scheduled_date || "");
  const rowId = `payment-${p.entry_id}`;
  const unpaid = p.status === "Forecast" || p.status === "Invoiced";
  return (
    <li className={`py-1.5 flex flex-wrap items-center gap-2 text-xs ${p.overdue ? "bg-rose-50/60 -mx-2 px-2 rounded" : ""}`} data-testid={rowId}>
      <Badge variant="outline" className={`text-[10px] ${PAY_BADGE[p.status] || ""}`}>{p.status}</Badge>
      {p.held && <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-300" data-testid={`${rowId}-held`}>Held</Badge>}
      <span className="text-slate-500 w-16">{p.category}</span>
      <span className="font-medium text-slate-800 tabular-nums w-28">{fmtMoney(p.amount, p.currency || deal.currency)}</span>
      {p.status === "Collected" && p.fx_rate_needed && (
        <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-300" data-testid={`${rowId}-rate-needed`} title="Collected in a foreign currency with no working rate on the day it arrived. It is left out of the naira total until a rate is set — never converted at today's rate.">
          <AlertTriangle className="w-3 h-3 mr-0.5" />rate needed
        </Badge>
      )}
      {p.status === "Collected" && p.amount_ngn != null && (p.currency && p.currency !== "NGN") && (
        <span className="text-[10px] text-emerald-700" data-testid={`${rowId}-ngn`} title={p.fx_rate_source || "frozen at collection"}>
          = {fmtMoney(p.amount_ngn, "NGN")} · frozen {p.fx_rate_date || ""}
        </span>
      )}
      {editingDate ? (
        <span className="flex items-center gap-1">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-6 text-xs w-36" data-testid={`${rowId}-date-input`} />
          <Button size="sm" variant="ghost" className="h-6 px-1.5" onClick={() => { onOverride(p.entry_id, { mode: "date", date }); setEditingDate(false); }} data-testid={`${rowId}-date-save`}><Check className="w-3 h-3" /></Button>
        </span>
      ) : p.held ? (
        <span className="tabular-nums inline-flex items-center gap-1 text-blue-700" data-testid={`${rowId}-date`}>
          Held{p.held_from_date ? ` · was ${p.held_from_date}` : ""}
        </span>
      ) : (
        <button type="button" className={`tabular-nums inline-flex items-center gap-1 hover:underline ${p.overdue ? "text-rose-600 font-semibold" : "text-slate-600"}`} onClick={() => setEditingDate(true)} title="Change due date" data-testid={`${rowId}-date`}>
          {p.overdue ? "Overdue · " : "Due "}{p.scheduled_date || "no date"}<PenLine className="w-3 h-3 opacity-50" />
        </button>
      )}
      {p.description && <span className="text-slate-400 truncate max-w-[220px]">{p.description}</span>}
      <span className="ml-auto flex items-center gap-1">
        {unpaid && (
          <Button size="sm" variant="outline" className="h-6 text-[11px] px-2 border-slate-300 text-slate-700 hover:bg-slate-50" disabled={busy} onClick={() => onOverride(p.entry_id, { mode: "due_now" })} data-testid={`${rowId}-due-now`}><Clock className="w-3 h-3 mr-1" />Due now</Button>
        )}
        {unpaid && !p.held && (
          <Button size="sm" variant="outline" className="h-6 text-[11px] px-2 border-blue-300 text-blue-700 hover:bg-blue-50" disabled={busy} onClick={() => onOverride(p.entry_id, { mode: "hold" })} data-testid={`${rowId}-hold`}><PauseCircle className="w-3 h-3 mr-1" />Hold</Button>
        )}
        {p.status === "Forecast" && (
          <Button size="sm" variant="outline" className="h-6 text-[11px] px-2 border-blue-300 text-blue-700 hover:bg-blue-50" disabled={busy} onClick={() => onUpdate(p.entry_id, { status: "Invoiced" })} data-testid={`${rowId}-mark-invoiced`}><Receipt className="w-3 h-3 mr-1" />Invoiced</Button>
        )}
        {(p.status === "Forecast" || p.status === "Invoiced") && (
          <Button size="sm" variant="outline" className="h-6 text-[11px] px-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50" disabled={busy} onClick={() => onUpdate(p.entry_id, { status: "Collected" })} data-testid={`${rowId}-mark-collected`}><Check className="w-3 h-3 mr-1" />Collected</Button>
        )}
        <Button size="sm" variant="ghost" className="h-6 px-1.5 text-slate-400 hover:text-rose-600" disabled={busy} onClick={() => onDelete(p.entry_id)} data-testid={`${rowId}-delete`}><Trash2 className="w-3 h-3" /></Button>
      </span>
    </li>
  );
}

const DELIVERY_BADGE = {
  running: "bg-emerald-50 text-emerald-700 border-emerald-300",
  signed_not_started: "bg-blue-50 text-blue-700 border-blue-300",
  paused: "bg-amber-50 text-amber-700 border-amber-300",
};

function DeliveryControls({ deal, onHoldDelivery, onStartDelivery, busy }) {
  const id = deal.opportunity_id;
  const [showStart, setShowStart] = useState(false);
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const state = deal.delivery_state || "running";
  const held = state === "signed_not_started" || state === "paused";
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`delivery-controls-${id}`}>
      <span className="text-slate-500">Delivery:</span>
      <Badge variant="outline" className={`text-[10px] ${DELIVERY_BADGE[state] || ""}`} data-testid={`delivery-state-${id}`}>{deal.delivery_state_label || "Running"}</Badge>
      {!held && (
        <>
          <Button size="sm" variant="outline" className="h-6 text-[11px] px-2 border-blue-300 text-blue-700 hover:bg-blue-50" disabled={busy} onClick={(e) => { e.stopPropagation(); onHoldDelivery(id, "signed_not_started"); }} data-testid={`delivery-signed-not-started-${id}`}><PauseCircle className="w-3 h-3 mr-1" />Signed, not started</Button>
          <Button size="sm" variant="outline" className="h-6 text-[11px] px-2 border-amber-300 text-amber-700 hover:bg-amber-50" disabled={busy} onClick={(e) => { e.stopPropagation(); onHoldDelivery(id, "paused"); }} data-testid={`delivery-pause-${id}`}><Pause className="w-3 h-3 mr-1" />Pause</Button>
        </>
      )}
      {held && !showStart && (
        <Button size="sm" variant="outline" className="h-6 text-[11px] px-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50" disabled={busy} onClick={(e) => { e.stopPropagation(); setShowStart(true); }} data-testid={`delivery-start-open-${id}`}><Play className="w-3 h-3 mr-1" />{state === "paused" ? "Resume" : "Start"} delivery</Button>
      )}
      {held && showStart && (
        <span className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-6 text-xs w-36" data-testid={`delivery-start-date-${id}`} />
          <Button size="sm" className="h-6 text-[11px] px-2 bg-emerald-600 hover:bg-emerald-700" disabled={busy || !startDate} onClick={() => { onStartDelivery(id, startDate); setShowStart(false); }} data-testid={`delivery-start-confirm-${id}`}><Check className="w-3 h-3 mr-1" />Confirm</Button>
        </span>
      )}
    </div>
  );
}

function AddPaymentForm({ deal, categories, onAdd, busy }) {
  const [form, setForm] = useState({ amount: "", scheduled_date: "", category: "Milestone", description: "", currency: deal.currency || "NGN" });
  const submit = async () => {
    if (!form.amount || !form.scheduled_date) return;
    await onAdd({ ...form, amount: parseFloat(form.amount), currency: form.currency || deal.currency || "NGN", status: "Forecast" });
    setForm({ amount: "", scheduled_date: "", category: "Milestone", description: "", currency: deal.currency || "NGN" });
  };
  return (
    <div className="flex flex-wrap items-center gap-2 pt-2 mt-2 border-t border-slate-100" data-testid={`add-payment-${deal.opportunity_id}`}>
      <Input type="number" placeholder={`Amount`} value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} className="h-7 text-xs w-28" data-testid={`add-payment-amount-${deal.opportunity_id}`} />
      <select value={form.currency} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))} className="h-7 text-xs border border-slate-200 rounded px-1.5 bg-white" data-testid={`add-payment-currency-${deal.opportunity_id}`} title="Each payment can be in its own currency">
        {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <Input type="date" value={form.scheduled_date} onChange={(e) => setForm((f) => ({ ...f, scheduled_date: e.target.value }))} className="h-7 text-xs w-36" data-testid={`add-payment-date-${deal.opportunity_id}`} />
      <select value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className="h-7 text-xs border border-slate-200 rounded px-1.5 bg-white" data-testid={`add-payment-category-${deal.opportunity_id}`}>
        {categories.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <Input placeholder="Note (optional)" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="h-7 text-xs flex-1 min-w-[140px]" data-testid={`add-payment-note-${deal.opportunity_id}`} />
      <Button size="sm" className="h-7 text-xs bg-slate-900 hover:bg-slate-800" disabled={busy || !form.amount || !form.scheduled_date} onClick={submit} data-testid={`add-payment-submit-${deal.opportunity_id}`}>
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Plus className="w-3 h-3 mr-1" />Add payment</>}
      </Button>
    </div>
  );
}

export function DealLedgerRow({ deal, categories, onAddPayment, onUpdatePayment, onDeletePayment, onMarkSigned, onHoldDelivery, onStartDelivery, onOverrideEntry, busy, canAct = true, onStatusChanged }) {
  const [open, setOpen] = useState(false);
  const id = deal.opportunity_id;
  const outstanding = deal.forecast + deal.invoiced;
  return (
    <>
      <tr className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${deal.overdue > 0 ? "bg-rose-50/40" : ""}`} onClick={() => setOpen((o) => !o)} data-testid={`deal-row-${id}`}>
        <td className="py-2 pl-2 w-6 text-slate-400">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</td>
        <td className="py-2">
          <p className="font-medium text-slate-900 leading-tight">{deal.company_name}</p>
          <p className="text-xs text-slate-500 truncate max-w-[260px]">{deal.opportunity_name}</p>
        </td>
        <td className="py-2"><Badge variant="outline" className={`text-[10px] ${BUCKET_BADGE[deal.bucket]}`} data-testid={`deal-bucket-${id}`}>{BUCKET_LABEL[deal.bucket]}</Badge><p className="text-[10px] text-slate-400 mt-0.5">{deal.stage}</p></td>
        <td className="py-2 pr-4 text-right tabular-nums font-semibold text-slate-900" data-testid={`deal-value-${id}`}>{fmtMoney(deal.value, deal.currency)}</td>
        <td className="py-2 pl-2 text-xs text-slate-600 tabular-nums">{deal.bucket === "signed" ? (deal.signed_date ? `Signed ${deal.signed_date}` : "Signed") : (deal.expected_close_date ? `Exp. ${deal.expected_close_date}` : "—")}</td>
        <td className="py-2 text-xs tabular-nums">{deal.next_due ? <span className="text-slate-700">{deal.next_due}</span> : <span className="text-slate-300">—</span>}</td>
        <td className="py-2 text-right tabular-nums text-xs">{deal.overdue > 0 ? <span className="text-rose-600 font-semibold" data-testid={`deal-overdue-${id}`}>{fmtMoney(deal.overdue, deal.currency)}</span> : <span className="text-slate-300">—</span>}</td>
        <td className="py-2 text-right tabular-nums text-xs text-blue-700">{outstanding > 0 ? fmtMoney(outstanding, deal.currency) : <span className="text-slate-300">—</span>}</td>
        <td className="py-2 pr-2 text-right tabular-nums text-xs text-emerald-700">{deal.collected > 0 ? fmtMoney(deal.collected, deal.currency) : <span className="text-slate-300">—</span>}</td>
      </tr>
      {open && (
        <tr className="bg-slate-50/60" data-testid={`deal-detail-${id}`}>
          <td />
          <td colSpan={8} className="pb-3 pr-2">
            <div className="rounded-md border border-slate-200 bg-white p-3">
              <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 mb-1">
                <span>Owner: <strong className="text-slate-700">{deal.owner || "—"}</strong></span>
                {deal.bucket === "signed" && deal.unscheduled > 0 && <span className="text-amber-700" data-testid={`deal-unscheduled-${id}`}>{fmtMoney(deal.unscheduled, deal.currency)} of the contract not yet scheduled</span>}
                {deal.bucket === "proposal" && (
                  <Button size="sm" variant="outline" className="ml-auto h-6 text-[11px] border-emerald-300 text-emerald-700 hover:bg-emerald-50" disabled={busy} onClick={(e) => { e.stopPropagation(); onMarkSigned(id); }} data-testid={`deal-mark-signed-${id}`}><Check className="w-3 h-3 mr-1" />Mark as signed</Button>
                )}
              </div>
              <div className="mb-2 pb-2 border-b border-slate-100" onClick={(e) => e.stopPropagation()}>
                <DealStatusControl dealId={id} currentStage={deal.stage} canAct={canAct}
                  testidPrefix={`fin-status-${id}`} compact onChanged={() => onStatusChanged && onStatusChanged()} />
              </div>
              {deal.bucket === "signed" && onHoldDelivery && (
                <div className="text-xs text-slate-500 mb-2 pb-2 border-b border-slate-100">
                  <DeliveryControls deal={deal} onHoldDelivery={onHoldDelivery} onStartDelivery={onStartDelivery} busy={busy} />
                  {(deal.delivery_state === "signed_not_started" || deal.delivery_state === "paused") && (
                    <p className="text-[11px] text-blue-700 mt-1" data-testid={`delivery-note-${id}`}>Unpaid milestones are held. They are never due or overdue until you start delivery. Use “Due now” on a line if one payment is genuinely due.</p>
                  )}
                </div>
              )}
              {deal.payments.length === 0 ? (
                <p className="text-xs text-slate-400 py-1">No payment schedule yet. Add the deposit, milestones and final payment below.</p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {deal.payments.map((p) => <PaymentLine key={p.entry_id} p={p} deal={deal} busy={busy} onUpdate={(eid, patch) => onUpdatePayment(id, eid, patch)} onDelete={(eid) => onDeletePayment(id, eid)} onOverride={(eid, payload) => onOverrideEntry(id, eid, payload)} />)}
                </ul>
              )}
              <AddPaymentForm deal={deal} categories={categories} busy={busy} onAdd={(payload) => onAddPayment(id, payload)} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
